import React, { useState, useEffect } from 'react';
import { useApiData } from '../../contexts/apiService';
import { MarketDetailsModal } from './MarketDetailsModal';
import Dropdown, { DropdownOption } from '../../components/common/dropdown/dropdown';
import { CustomBarChart } from './CustomBarChart';
import { useDataManager } from "../../contexts/dataManager";
import '../../components/css_files/monthNavButton.css';

interface NominationsData {
  deliveredVol: number;
  nominatedVol: number;
  expectedVol: number;
}

interface ApiData {
  crude_main_delivery: any[];
  nominations: any[];
}

const today = new Date();
const currentMonthName = today.toLocaleString('en-US', { month: 'long' });
const year = today.getFullYear();
const todayMonthDate = `${year}-${currentMonthName}`;

const DailySummary: React.FC = () => {
  const { apiData, isLoading } = useApiData();
  const [nominationsData, setNominationsData] = useState<{ [key: string]: NominationsData }>({});
  const [currentMonth, setCurrentMonth] = useState(todayMonthDate);
  const [monthOptions, setMonthOptions] = useState<DropdownOption[]>([]);
  const [isFirstLoading, setIsFirstLoading] = useState<boolean>(true);
  const [selectedMarket, setSelectedMarket] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const { requestTableData, hasTableData, isTableLoading } = useDataManager();

  useEffect(() => {
    if (!hasTableData('nominations')) {
      requestTableData('nominations');
    }
  }, []);


  const typedApiData = apiData ? (apiData as unknown as ApiData) : null;

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const getProcessedData = (
    nominatedVolume: Record<string, number>,
    nominationDestination: Array<{ station: string; market?: string; key?: string }> | undefined,
    apiNominationStations: Record<string, { delivered?: number; lastMonth?: { delivered: number } }>

  ): Record<string, { deliveredVol: number; nominatedVol: number; expectedVol: number }> => {

    return nominationDestination?.reduce((acc: Record<string, { deliveredVol: number; nominatedVol: number; expectedVol: number }>, nomItem) => {
      const stationDestination = nomItem.key || nomItem.station;
      if (nominatedVolume[stationDestination]) {
        const deliveredVol = apiNominationStations[stationDestination]?.delivered || 0;
        const nominatedVol = nominatedVolume[stationDestination] || 0;

        let expectedVol = 0;

        if (apiNominationStations[stationDestination]?.lastMonth) {
          const delVolPreviousMonth = apiNominationStations[stationDestination].lastMonth!.delivered;
          const today = new Date();
          const currentDay = today.getDate();
          const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
          const daysRemaining = daysInMonth - currentDay;
          const totalDelivered = delVolPreviousMonth + deliveredVol;
          const averageDaily = totalDelivered / (currentDay + daysInMonth);
          expectedVol = (averageDaily * daysRemaining) + deliveredVol;
        }

        acc[stationDestination] = { deliveredVol, nominatedVol, expectedVol };
      }
      return acc;
    }, {}) || {};
  };

  const getParsedDate = (currentDate: string) => {
    const [year, month] = currentDate.split("-");
    return `${month} ${year}`;
  };

  // Convert a "YYYY-MonthName" month key (e.g. "2026-July") into a Date.
  const monthKeyToDate = (key: string) => {
    const [ky, kmRaw] = key.split('-');
    const km = kmRaw?.trim();
    const monthIndex = ['January','February','March','April','May','June','July','August','September','October','November','December'].indexOf(km);
    return monthIndex === -1 ? new Date(NaN) : new Date(Number(ky), monthIndex, 1);
  };

  const requestCrudeWithPrevMonth = (monthKey: string) => {
    const currentDate = getParsedDate(monthKey);
    requestTableData('crude_main_delivery', currentDate);
    if (monthKey === todayMonthDate) {
      const [monthName, yearStr] = currentDate.split(" ");
      const prev = new Date(`${monthName} 1, ${yearStr}`);
      prev.setMonth(prev.getMonth() - 1);
      const prevDate = `${prev.toLocaleString('en-US', { month: 'long' })} ${prev.getFullYear()}`;
      requestTableData('crude_main_delivery', prevDate);
    }
  };

  const handleMonthDropdownChange = (selectedValue: string) => {
    setCurrentMonth(selectedValue);
    requestCrudeWithPrevMonth(selectedValue);
  };

  const handleNextMonth = () => {
    const nextMonthIndex = monthOptions.findIndex(item => item.value === currentMonth) - 1;
    if (monthOptions[nextMonthIndex]) {
      const nextMonth = monthOptions[nextMonthIndex].value;
      setCurrentMonth(nextMonth);
      requestCrudeWithPrevMonth(nextMonth);
    }
  };

  const handlePreviousMonth = () => {
    const previousMonthIndex = monthOptions.findIndex(item => item.value === currentMonth) + 1;
    if (monthOptions[previousMonthIndex]) {
      const previousMonth = monthOptions[previousMonthIndex].value;
      setCurrentMonth(previousMonth);
      requestCrudeWithPrevMonth(previousMonth);
    }
  };

  const isNextMonthDisabled = () => {
    if (!currentMonth) return true;
    return currentMonth === monthOptions[0]?.value;
  };

  const isPreviousMonthDisabled = () => {
    if (!currentMonth) return true;
    return currentMonth === monthOptions[monthOptions.length - 1]?.value;
  };

  // Build the month navigator. It must include recent months that have DELIVERY
  // data but no nominations (e.g. June/May) — otherwise they'd be unreachable
  // and the "fall back to the previous month's nominations" logic below could
  // never run for them. This is pure computation: it does NOT fetch anything up
  // front — each month's crude data is only fetched on demand when the user
  // opens that month.
  useEffect(() => {
    if (isLoading || !typedApiData?.nominations) return;

    const monthSort = new Map<string, number>(); // month key -> sort timestamp
    const addMonthKey = (value: string) => {
      if (monthSort.has(value)) return;
      const time = monthKeyToDate(value).getTime();
      if (isNaN(time)) return;
      monthSort.set(value, time);
    };

    // 1) Months that already have nominations (exact keys, as before)
    typedApiData.nominations.forEach((n: any) => addMonthKey(`${n.year}-${n.month}`));

    // 2) A rolling window of the last 24 months (incl. the current month), so
    //    recent months without nominations stay navigable.
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      addMonthKey(`${d.getFullYear()}-${d.toLocaleString('en-US', { month: 'long' })}`);
    }

    const dropdownMonths = Array.from(monthSort.entries())
      .sort((a, b) => b[1] - a[1]) // most recent first
      .map(([value]) => {
        const [yr, mo] = value.split('-');
        return { value, label: `${mo} ${yr}` };
      });

    setMonthOptions(dropdownMonths);

    // One-time initial selection (most recent month) + crude fetch.
    if (isFirstLoading && dropdownMonths.length > 0) {
      const mostRecentMonth = dropdownMonths[0].value;
      setCurrentMonth(mostRecentMonth);
      requestCrudeWithPrevMonth(mostRecentMonth);
      setIsFirstLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, typedApiData?.nominations]);

  useEffect(() => {
    if (!isLoading && typedApiData && typedApiData.nominations) {
      // The month navigator and initial month selection are handled by the
      // navigator effect above; wait until that first-load setup has finished.
      if (isFirstLoading) return;

      const isCurrentMonthSelected = todayMonthDate === currentMonth;
      const date = new Date();
      date.setMonth(date.getMonth() - 1);
      const previousMonthName = date.toLocaleString('en-US', { month: 'long' });
      const previousYear = date.getFullYear();

      const nominatedVolumesFromApi = typedApiData.nominations.reduce((acc, nomination) => {
        const key = `${nomination.year}-${nomination.month}`;

        if (!acc[key]) {
          acc[key] = {};
        }

        if (!acc["nominationsDestination"]) {
          acc["nominationsDestination"] = [];

        }

        const isAlreadyInArray = acc["nominationsDestination"].some(
          (item: { station: string, market: string }) => item.station === nomination.station && item.market === nomination.market
        );

        if (!isAlreadyInArray) {
          acc["nominationsDestination"].push({ station: nomination.station, market: nomination.market });
        }

        acc[key][nomination.station] = Number(nomination.nomination_monthly);
        return acc;
      }, {});

      // Detect stations with multiple markets (e.g. Calumet → Shell & Total)
      const stationMarkets: Record<string, Set<string>> = {};
      (nominatedVolumesFromApi["nominationsDestination"] || []).forEach(
        (item: { station: string; market: string }) => {
          if (!stationMarkets[item.station]) stationMarkets[item.station] = new Set();
          stationMarkets[item.station].add(item.market);
        }
      );

      // Add display key to each nomination destination entry
      nominatedVolumesFromApi["nominationsDestination"] = (nominatedVolumesFromApi["nominationsDestination"] || []).map(
        (item: { station: string; market: string }) => ({
          ...item,
          key: stationMarkets[item.station]?.size > 1 ? `${item.station} - ${item.market}` : item.station
        })
      );

      // Rebuild nominated volumes with composite keys for multi-market stations
      const rebuiltVolumes: Record<string, Record<string, number>> = {};
      typedApiData.nominations.forEach((nomination: any) => {
        const monthKey = `${nomination.year}-${nomination.month}`;
        const hasMultipleMarkets = stationMarkets[nomination.station]?.size > 1;
        const displayKey = hasMultipleMarkets ? `${nomination.station} - ${nomination.market}` : nomination.station;
        if (!rebuiltVolumes[monthKey]) rebuiltVolumes[monthKey] = {};
        rebuiltVolumes[monthKey][displayKey] = Number(nomination.nomination_monthly);
      });
      Object.keys(rebuiltVolumes).forEach(monthKey => {
        nominatedVolumesFromApi[monthKey] = rebuiltVolumes[monthKey];
      });

      const { nominationsDestination, ...nominatedVolumes } = nominatedVolumesFromApi;

      const [month, year] = getParsedDate(currentMonth).split(" ");

      const nominationDestinationFromApi = (typedApiData.crude_main_delivery || []).reduce(
        (acc: { [key: string]: { delivered: number; data: any[]; lastMonth?: { delivered: number; data: any[] } } }, item: any) => {
          const destination = item.destination;

          if (!destination) {
            return acc;
          }

          if (item.month === month && item.year == Number(year)) {
            const nominationsDestinationIndex = nominationsDestination.findIndex(
              (nomItem: { station: string; market: string; key?: string }) => {
                const stationMatch = nomItem.station.toUpperCase() === destination.toUpperCase();
                const marketMatch = nomItem.market.toUpperCase() === destination.toUpperCase();
                if (stationMatch && stationMarkets[nomItem.station]?.size > 1) {
                  return item.market && nomItem.market.toUpperCase() === item.market.toUpperCase();
                }
                return stationMatch || marketMatch;
              }
            );

            if (nominationsDestinationIndex !== -1) {
              const stationKey = nominationsDestination[nominationsDestinationIndex].key || nominationsDestination[nominationsDestinationIndex].station;

              if (!acc[stationKey]) {
                acc[stationKey] = { delivered: 0, data: [] };
              }

              acc[stationKey].delivered += Number(item.gross_barrels);
              acc[stationKey].data.push(item);
            }
          }

          if (isCurrentMonthSelected && item.month === previousMonthName && item.year == previousYear) {
            const nominationsDestinationIndex = nominationsDestination.findIndex(
              (nomItem: { station: string; market: string; key?: string }) => {
                const stationMatch = nomItem.station.toUpperCase() === destination.toUpperCase();
                const marketMatch = nomItem.market.toUpperCase() === destination.toUpperCase();
                if (stationMatch && stationMarkets[nomItem.station]?.size > 1) {
                  return item.market && nomItem.market.toUpperCase() === item.market.toUpperCase();
                }
                return stationMatch || marketMatch;
              }
            );

            if (nominationsDestinationIndex !== -1) {
              const stationKey = nominationsDestination[nominationsDestinationIndex].key || nominationsDestination[nominationsDestinationIndex].station;

              if (!acc[stationKey]) {
                acc[stationKey] = { delivered: 0, data: [], lastMonth: { delivered: 0, data: [] } };
              }

              if (!acc[stationKey].lastMonth) {
                acc[stationKey].lastMonth = { delivered: 0, data: [] };
              }

              acc[stationKey].lastMonth!.delivered += Number(item.gross_barrels);
              acc[stationKey].lastMonth!.data.push(item);
            }
          }

          return acc;
        },
        {} as { [key: string]: { delivered: number; data: any[]; lastMonth?: { delivered: number; data: any[] } } }
      );

      // No nominations loaded for the selected month → fall back to the most
      // recent month that has nominations at or before the selected month
      // (i.e. the previous month's nominations). If none exists (the selected
      // month predates every nomination), use the most recent available.
      let nominatedVolumeToUse = nominatedVolumes[currentMonth];
      if (!nominatedVolumeToUse || Object.keys(nominatedVolumeToUse).length === 0) {
        const selectedTime = monthKeyToDate(currentMonth).getTime();
        const monthsWithNoms = Object.keys(nominatedVolumes)
          .filter(k => nominatedVolumes[k] && Object.keys(nominatedVolumes[k]).length > 0)
          .map(k => ({ key: k, time: monthKeyToDate(k).getTime() }))
          .filter(x => !isNaN(x.time))
          .sort((a, b) => b.time - a.time); // most recent first
        const previous = monthsWithNoms.find(
          x => isNaN(selectedTime) || x.time <= selectedTime
        );
        const chosen = previous || monthsWithNoms[0];
        if (chosen) {
          nominatedVolumeToUse = nominatedVolumes[chosen.key];
        }
      }

      setNominationsData(getProcessedData(nominatedVolumeToUse || {}, nominationsDestination, nominationDestinationFromApi));
    }
  }, [isLoading, isTableLoading('nominations'), isTableLoading('crude_main_delivery'), typedApiData?.crude_main_delivery, currentMonth, isFirstLoading]);

  const filteredData = typedApiData?.crude_main_delivery?.filter(
    (item: any) => {
      const [month, year] = getParsedDate(currentMonth).split(" ");
      if (selectedMarket?.includes(' - ')) {
        const [station, market] = selectedMarket.split(' - ');
        return item.destination === station && item.market === market && item.month === month && item.year == Number(year);
      }
      return item.destination === selectedMarket && item.month === month && item.year == Number(year);
    });

  const transformedData = filteredData?.map((item: any) => ({
    destination: item.destination,
    load_date: item.load_date,
    ticket_number: item.ticket_number,
    lease: item.lease,
    gross_barrels: item.gross_barrels,
    net_barrels: item.net_barrels,
    carrier: item.carrier_name,
  })) || [];

  const markets = Object.keys(nominationsData);
  const labels = markets.map(market => market);
  const nominatedData = markets.map(market => nominationsData[market].nominatedVol);
  const deliveredData = markets.map(market => nominationsData[market].deliveredVol);
  const expectedData = markets.map(market => nominationsData[market].expectedVol);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div>
        <div className="month-navigation">
          <button className="nav-button" disabled={isPreviousMonthDisabled()} onClick={handlePreviousMonth}>Previous</button>
          <Dropdown
            options={monthOptions}
            value={currentMonth}
            onChange={handleMonthDropdownChange}
          />
          <button className="nav-button" onClick={handleNextMonth} disabled={isNextMonthDisabled()}>Next</button>
        </div>
        <CustomBarChart
          labels={labels}
          nominatedData={nominatedData}
          deliveredData={deliveredData}
          expectedData={expectedData}
          setSelectedMarket={setSelectedMarket}
          setShowModal={setShowModal}
          currentMonth={currentMonth}
        />
        <MarketDetailsModal
          show={showModal}
          onHide={handleCloseModal}
          size="xl"
          centered
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            maxWidth: 'none',
          }}
          data={transformedData}

        />
      </div>
    </div>
  );
};
export default DailySummary;
